import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaCalendarTimes,
  FaMoneyBillWave,
  FaClock,
  FaExclamationTriangle,
  FaCheckCircle,
  FaTimesCircle,
  FaPercentage,
  FaPhoneAlt
} from "react-icons/fa";

const CancellationPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 pt-20 md:pt-24 pb-12">
      <div className="w-full max-w-[1180px] mx-auto px-4 md:px-6">
        {/* Back Button */}
        <div className="mb-3 md:mb-6">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center text-primary hover:text-primary-dark transition-colors font-medium group"
          >
            <FaArrowLeft className="mr-2 group-hover:-translate-x-1 transition-transform" />
            Go Back
          </button>
        </div>

        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-5 md:p-8 mb-4 md:mb-8">
          <div className="flex items-center mb-4">
            <FaCalendarTimes className="text-primary text-3xl mr-4" />
            <div>
              <h1 className="text-2xl md:text-4xl font-bold text-gray-800">
                Cancellation Policy
              </h1>
              <p className="text-gray-600 text-base md:text-lg mt-1 md:mt-2">
                Learn about our cancellation and refund policies for all booking types
              </p>
            </div>
          </div>
          <div className="text-sm text-gray-500">
            Last updated: 15 Aug 2025
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-sm p-5 md:p-8">

          {/* Section 1: Overview */}
          <section className="mb-6 md:mb-8">
            <h2 className="text-lg md:text-2xl font-semibold text-gray-800 mb-3 md:mb-4 flex items-center">
              <FaExclamationTriangle className="text-yellow-500 mr-3" />
              1. Cancellation Overview
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We understand that plans can change. Our cancellation policy is designed
              to be fair to both our customers and our driver partners. Please review
              the cancellation rules based on your booking type and timing.
            </p>

            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-gray-800 mb-2 flex items-center">
                <FaClock className="text-blue-600 mr-2" />
                Free Cancellation Window
              </h3>
              <p className="text-gray-700 text-sm">
                All bookings can be cancelled <strong>free of charge</strong> up to
                <strong> 1 hour</strong> before the scheduled pickup time. Cancellations
                made within this window will receive a full refund.
              </p>
            </div>
          </section>

          {/* Section 2: Cancellation Timeframes */}
          <section className="mb-6 md:mb-8">
            <h2 className="text-lg md:text-2xl font-semibold text-gray-800 mb-3 md:mb-4 flex items-center">
              <FaClock className="text-purple-500 mr-3" />
              2. Cancellation Timeframes & Charges
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              The cancellation charges vary depending on when you cancel your booking:
            </p>

            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="text-left p-3 font-semibold border">Cancellation Time</th>
                    <th className="text-left p-3 font-semibold border">Charge</th>
                    <th className="text-left p-3 font-semibold border">Refund Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr className="bg-green-50">
                    <td className="p-3 border">
                      <FaCheckCircle className="inline text-green-600 mr-2" />
                      More than 1 hour before pickup
                    </td>
                    <td className="p-3 border text-green-600 font-semibold">No Charge</td>
                    <td className="p-3 border">100% refund</td>
                  </tr>
                  <tr className="bg-yellow-50">
                    <td className="p-3 border">
                      <FaExclamationTriangle className="inline text-yellow-600 mr-2" />
                      30 minutes - 1 hour before pickup
                    </td>
                    <td className="p-3 border text-yellow-600 font-semibold">25% of fare</td>
                    <td className="p-3 border">75% refund</td>
                  </tr>
                  <tr className="bg-orange-50">
                    <td className="p-3 border">
                      <FaTimesCircle className="inline text-orange-600 mr-2" />
                      Less than 30 minutes before pickup
                    </td>
                    <td className="p-3 border text-orange-600 font-semibold">50% of fare</td>
                    <td className="p-3 border">50% refund</td>
                  </tr>
                  <tr className="bg-red-50">
                    <td className="p-3 border">
                      <FaTimesCircle className="inline text-red-600 mr-2" />
                      After pickup / No-show
                    </td>
                    <td className="p-3 border text-red-600 font-semibold">100% of fare</td>
                    <td className="p-3 border">No refund</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Section 3: Service-Specific Policies */}
          <section className="mb-6 md:mb-8">
            <h2 className="text-lg md:text-2xl font-semibold text-gray-800 mb-3 md:mb-4 flex items-center">
              <FaPercentage className="text-green-500 mr-3" />
              3. Service-Specific Cancellation Rules
            </h2>

            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h3 className="font-semibold text-gray-800 mb-2">Outstation / One Way Trips</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li>• Free cancellation up to 4 hours before pickup</li>
                  <li>• 50% charge for cancellations within 4 hours</li>
                  <li>• Full fare charged for no-shows</li>
                </ul>
              </div>

              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h3 className="font-semibold text-gray-800 mb-2">Round Trip Bookings</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li>• Free cancellation up to 24 hours before first pickup</li>
                  <li>• 25% charge for cancellations within 24-4 hours</li>
                  <li>• 50% charge for cancellations within 4 hours</li>
                </ul>
              </div>

              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h3 className="font-semibold text-gray-800 mb-2">Local Rentals (Hourly Packages)</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li>• Free cancellation up to 1 hour before pickup</li>
                  <li>• 50% charge for cancellations within 1 hour</li>
                  <li>• Full charge for cancellations after driver arrival</li>
                </ul>
              </div>

              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h3 className="font-semibold text-gray-800 mb-2">Airport Transfers</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li>• Free cancellation up to 2 hours before pickup</li>
                  <li>• 50% charge for cancellations within 2 hours</li>
                  <li>• Full charge for flight delays beyond 2 hours without intimation</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 4: Refund Process */}
          <section className="mb-6 md:mb-8">
            <h2 className="text-lg md:text-2xl font-semibold text-gray-800 mb-3 md:mb-4 flex items-center">
              <FaMoneyBillWave className="text-blue-500 mr-3" />
              4. Refund Process
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Refunds are processed automatically based on the cancellation timing:
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex items-start">
                  <div className="bg-green-100 p-2 rounded-full mr-3 mt-1">
                    <FaCheckCircle className="text-green-600 text-sm" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 text-sm">Automatic Refunds</h4>
                    <p className="text-gray-600 text-xs">
                      Refunds to original payment method within 5-7 business days
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-blue-100 p-2 rounded-full mr-3 mt-1">
                    <FaMoneyBillWave className="text-blue-600 text-sm" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 text-sm">Wallet Refunds</h4>
                    <p className="text-gray-600 text-xs">
                      Refunds to CabZigo wallet are instant and can be used for future bookings
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-start">
                  <div className="bg-yellow-100 p-2 rounded-full mr-3 mt-1">
                    <FaPercentage className="text-yellow-600 text-sm" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 text-sm">Partial Refunds</h4>
                    <p className="text-gray-600 text-xs">
                      After deducting applicable cancellation charges
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-purple-100 p-2 rounded-full mr-3 mt-1">
                    <FaClock className="text-purple-600 text-sm" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 text-sm">Processing Time</h4>
                    <p className="text-gray-600 text-xs">
                      Bank refunds may take 5-7 business days to reflect
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 5: Driver Cancellation */}
          <section className="mb-6 md:mb-8">
            <h2 className="text-lg md:text-2xl font-semibold text-gray-800 mb-3 md:mb-4">
              5. Driver Cancellation Policy
            </h2>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-700 leading-relaxed mb-4">
                In rare cases where a driver cancels the trip:
              </p>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>• You will be assigned a new driver at no extra cost</li>
                <li>• If no replacement is available, full refund will be processed</li>
                <li>• You may receive a discount coupon for the inconvenience</li>
                <li>• Repeated driver cancellations are tracked to maintain service quality</li>
              </ul>
            </div>
          </section>

          {/* Section 6: Special Cases */}
          <section className="mb-6 md:mb-8">
            <h2 className="text-lg md:text-2xl font-semibold text-gray-800 mb-3 md:mb-4">
              6. Special Circumstances
            </h2>

            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-800 mb-2">Emergency Situations</h3>
                <p className="text-gray-700 text-sm">
                  In case of medical emergencies, natural disasters, or unavoidable
                  circumstances, please contact our support team. We review such cases
                  on an individual basis and may waive cancellation charges with proper documentation.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-800 mb-2">Corporate Bookings</h3>
                <p className="text-gray-700 text-sm">
                  Corporate account bookings follow the same cancellation policy.
                  Refunds are credited to the corporate wallet for future use.
                </p>
              </div>
            </div>
          </section>

          {/* Contact Section */}
          <section className="bg-gray-50 p-4 md:p-6 rounded-lg mt-6 md:mt-8">
            <h2 className="text-lg md:text-2xl font-semibold text-gray-800 mb-3 md:mb-4 flex items-center">
              <FaPhoneAlt className="text-primary mr-3" />
              Need Help with Cancellation?
            </h2>
            <p className="text-gray-700 mb-4">
              If you need to cancel a booking or have questions about refunds,
              please contact our support team:
            </p>
            <div className="space-y-2 text-gray-700">
              <p>
                <strong>Email:</strong> info@rajeshwarinfotech.com
              </p>
              <p>
                <strong>Phone:</strong> +91 7096386636
              </p>
              <p>
                <strong>Support Hours:</strong> 24/7 for urgent cancellations
              </p>
            </div>
          </section>
        </div>

        {/* Related Links */}
        <div className="mt-4 md:mt-8 text-center">
          <div className="inline-flex space-x-4">
            <Link
              to="/privacy"
              className="text-primary hover:text-primary-dark underline"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms"
              className="text-primary hover:text-primary-dark underline"
            >
              Terms & Conditions
            </Link>
            <Link
              to="/refund"
              className="text-primary hover:text-primary-dark underline"
            >
              Refund Policy
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CancellationPolicy;
